import React from "react";
import { FormikProps, getIn } from "formik";
import { IconType } from "enums/Icon";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import {
  CountryOption,
  getBillingAddressCountriesOptions,
} from "utils/countries";
import {
  createFieldBlurHandler,
  createMaskedFieldBlurHandler,
} from "utils/formValidation";
import amexLogo from "./icons/amex.svg";
import mastercardLogo from "./icons/mastercard.svg";
import visaLogo from "./icons/visa.svg";
import { ButtonTone } from "../Button/Button.style";
import Icon from "../Icon/Icon";
import IconButton from "../IconButton";
import Input from "../Input/Input";
import Select from "../Select/Select";
import Tooltip from "../Tooltip/Tooltip";
import {
  AddressSection,
  BrandAdornment,
  BrandLogo,
  AddressTripleFields,
  AdornmentIcon,
  CardForm,
  CardSection,
  CardSectionFields,
  Fields,
  InputAdornment,
  SectionTitle,
  SecureNotice,
  SplitFields,
  TooltipContent,
} from "./CreditCardForm.styles";
import {
  CreditCardBrand,
  detectCreditCardBrand,
} from "./creditCardFormHelper";

type CreditCardFieldName = "name" | "number" | "expiration" | "cvv";
type BillingAddressFieldName =
  | "country"
  | "address1"
  | "address2"
  | "city"
  | "state"
  | "postalCode";

type CreditCardFieldPaths = Record<CreditCardFieldName, string> & {
  billing: Record<BillingAddressFieldName, string>;
};

type CreditCardFormFieldPaths = Partial<Record<CreditCardFieldName, string>> & {
  billing?: Partial<Record<BillingAddressFieldName, string>>;
};

const DEFAULT_FIELD_PATHS: CreditCardFieldPaths = {
  name: "name",
  number: "number",
  expiration: "expiration",
  cvv: "cvv",
  billing: {
    country: "billingAddress.country",
    address1: "billingAddress.address1",
    address2: "billingAddress.address2",
    city: "billingAddress.city",
    state: "billingAddress.state",
    postalCode: "billingAddress.postalCode",
  },
};

interface CreditCardFormProps<TValues extends object> {
  formik: FormikProps<TValues>;
  fields?: CreditCardFormFieldPaths;
  cardSectionHeader?: React.ReactNode | null;
  cardSectionFooter?: React.ReactNode;
  fieldsTopSpacing?: "default" | "section";
}

interface CreditCardWidgetFieldsProps<TValues extends object> {
  formik: FormikProps<TValues>;
  fieldPaths: Record<CreditCardFieldName, string>;
}

interface BillingAddressFieldsProps<TValues extends object> {
  formik: FormikProps<TValues>;
  fieldPaths: Record<BillingAddressFieldName, string>;
}

const CARD_BRAND_ASSETS: Record<
  CreditCardBrand,
  { src: string; alt: string }
> = {
  visa: {
    src: visaLogo,
    alt: "Visa",
  },
  mastercard: {
    src: mastercardLogo,
    alt: "Mastercard",
  },
  amex: {
    src: amexLogo,
    alt: "American Express",
  },
};

const DEFAULT_CARD_NUMBER_MASK = "9999 9999 9999 9999";
const AMEX_CARD_NUMBER_MASK = "9999 999999 99999";

const CreditCardWidgetFields = <TValues extends object>({
  formik,
  fieldPaths,
}: CreditCardWidgetFieldsProps<TValues>) => {
  const { values, handleChange, errors, touched } = formik;
  const handleBlur = createFieldBlurHandler(formik);
  const handleExpirationBlur = createMaskedFieldBlurHandler(formik, "99/9999");
  const { t } = useTranslation("common");

  const stringValue = (path: string) =>
    (getIn(values, path) as string | undefined) || "";
  const fieldError = (path: string) => getIn(errors, path) as string | undefined;
  const isFieldTouched = (path: string) => !!getIn(touched, path);
  const numberPath = fieldPaths.number;
  const expirationPath = fieldPaths.expiration;
  const cvvPath = fieldPaths.cvv;
  const namePath = fieldPaths.name;
  const numberValue = stringValue(numberPath);
  const numberError = fieldError(numberPath);
  const isNumberInvalid = isFieldTouched(numberPath) && !!numberError;
  const cvvError = fieldError(cvvPath);
  const isCvvInvalid = isFieldTouched(cvvPath) && !!cvvError;
  const detectedBrand = React.useMemo(
    () => detectCreditCardBrand(numberValue),
    [numberValue],
  );
  const cardNumberMask =
    detectedBrand === "amex" ? AMEX_CARD_NUMBER_MASK : DEFAULT_CARD_NUMBER_MASK;
  const handleCardNumberBlur = React.useMemo(
    () => createMaskedFieldBlurHandler(formik, cardNumberMask),
    [formik, cardNumberMask],
  );
  const renderAdornment = ({
    iconType,
    tooltip,
    ariaLabel,
    tone,
  }: {
    iconType: IconType;
    tooltip?: string;
    ariaLabel: string;
    tone?: ButtonTone;
  }) => {
    if (!tooltip) {
      return (
        <InputAdornment>
          <AdornmentIcon type={iconType} />
        </InputAdornment>
      );
    }

    return (
      <Tooltip body={<TooltipContent>{tooltip}</TooltipContent>} place="top">
        <IconButton compact tone={tone} aria-label={ariaLabel}>
          <Icon type={iconType} />
        </IconButton>
      </Tooltip>
    );
  };
  const renderBrandAdornment = (brand: CreditCardBrand) => {
    const { src, alt } = CARD_BRAND_ASSETS[brand];

    return (
      <BrandAdornment>
        <BrandLogo src={src} alt={alt} />
      </BrandAdornment>
    );
  };
  const numberAdornment = isNumberInvalid
    ? renderAdornment({
        iconType: IconType.Wrong,
        tooltip: numberError,
        ariaLabel: t("purchase.creditCard.number"),
        tone: "danger",
      })
    : detectedBrand
      ? renderBrandAdornment(detectedBrand)
      : renderAdornment({
          iconType: IconType.LockSimpleLight,
          ariaLabel: t("purchase.creditCard.number"),
        });

  return (
    <Fields>
      <InputMask
        placeholder={t("purchase.creditCard.number")}
        name={numberPath}
        value={numberValue}
        onChange={handleChange}
        onBlur={handleCardNumberBlur}
        mask={cardNumberMask}
      >
        {/* @ts-ignore */}
        {(inputProps) => (
          <Input
            {...inputProps}
            invalid={isNumberInvalid}
            endAdornment={numberAdornment}
            reserveEndAdornmentSpace
            centerEndAdornment
          />
        )}
      </InputMask>
      <SplitFields>
        <InputMask
          placeholder={t("purchase.creditCard.expiration")}
          name={expirationPath}
          value={stringValue(expirationPath)}
          onChange={handleChange}
          onBlur={handleExpirationBlur}
          mask={"99/9999"}
        >
          {/* @ts-ignore */}
          {(inputProps) => (
            <Input
              {...inputProps}
              tooltip={fieldError(expirationPath)}
              invalid={isFieldTouched(expirationPath) && !!fieldError(expirationPath)}
            />
          )}
        </InputMask>
        <Input
          placeholder={t("purchase.creditCard.cvv")}
          name={cvvPath}
          value={stringValue(cvvPath)}
          onChange={handleChange}
          onBlur={handleBlur}
          invalid={isCvvInvalid}
          endAdornment={renderAdornment(
            isCvvInvalid
              ? {
                  iconType: IconType.Wrong,
                  tooltip: cvvError,
                  ariaLabel: t("purchase.creditCard.cvv"),
                  tone: "danger",
                }
              : {
                  iconType: IconType.QuestionLight,
                  tooltip: t("purchase.creditCard.cvvTooltip"),
                  ariaLabel: t("purchase.creditCard.cvv"),
                },
          )}
          reserveEndAdornmentSpace
          centerEndAdornment
        />
      </SplitFields>
      <Input
        placeholder={t("purchase.creditCard.name")}
        name={namePath}
        value={stringValue(namePath)}
        onChange={handleChange}
        onBlur={handleBlur}
        tooltip={fieldError(namePath)}
        invalid={isFieldTouched(namePath) && !!fieldError(namePath)}
      />
    </Fields>
  );
};

const BillingAddressFields = <TValues extends object>({
  formik,
  fieldPaths,
}: BillingAddressFieldsProps<TValues>) => {
  const { values, handleChange, errors, touched, setFieldValue, setFieldTouched } =
    formik;
  const handleBlur = createFieldBlurHandler(formik);
  const { t, i18n } = useTranslation("common");
  const countriesOptions = React.useMemo(
    () => getBillingAddressCountriesOptions(i18n.language),
    [i18n.language],
  );

  const stringValue = (path: string) =>
    (getIn(values, path) as string | undefined) || "";
  const optionValue = (path: string) =>
    (getIn(values, path) as CountryOption | null | undefined) || null;
  const fieldError = (path: string) => getIn(errors, path) as string | undefined;
  const isFieldTouched = (path: string) => !!getIn(touched, path);
  const countryPath = fieldPaths.country;
  const address1Path = fieldPaths.address1;
  const address2Path = fieldPaths.address2;
  const cityPath = fieldPaths.city;
  const statePath = fieldPaths.state;
  const postalCodePath = fieldPaths.postalCode;
  const billingCountry = optionValue(countryPath);
  const stateOrRegion = billingCountry?.value === "US" ? "US" : "other";
  const handleCountryChange = React.useCallback(
    (value: unknown) => {
      setFieldValue(countryPath, value as CountryOption | null);
    },
    [countryPath, setFieldValue],
  );
  const handleCountryBlur = React.useCallback(() => {
    const value = getIn(values, countryPath);

    setFieldTouched(countryPath, !!value);
  }, [countryPath, setFieldTouched, values]);

  return (
    <Fields>
      <Select
        placeholder={t("purchase.creditCard.countryRegion")}
        name={countryPath}
        options={countriesOptions}
        value={billingCountry}
        onChange={handleCountryChange}
        onBlur={handleCountryBlur}
        invalid={isFieldTouched(countryPath) && !!fieldError(countryPath)}
      />
      <Input
        placeholder={t("purchase.creditCard.address")}
        name={address1Path}
        value={stringValue(address1Path)}
        onChange={handleChange}
        onBlur={handleBlur}
        tooltip={fieldError(address1Path)}
        invalid={isFieldTouched(address1Path) && !!fieldError(address1Path)}
      />
      <Input
        placeholder={t("purchase.creditCard.address2")}
        name={address2Path}
        value={stringValue(address2Path)}
        onChange={handleChange}
        onBlur={handleBlur}
        tooltip={fieldError(address2Path)}
        invalid={isFieldTouched(address2Path) && !!fieldError(address2Path)}
      />
      <AddressTripleFields>
        <Input
          placeholder={t("auth.city")}
          name={cityPath}
          value={stringValue(cityPath)}
          onChange={handleChange}
          onBlur={handleBlur}
          tooltip={fieldError(cityPath)}
          invalid={isFieldTouched(cityPath) && !!fieldError(cityPath)}
        />
        <Input
          placeholder={t(`auth.stateOrRegion.${stateOrRegion}`)}
          name={statePath}
          value={stringValue(statePath)}
          onChange={handleChange}
          onBlur={handleBlur}
          tooltip={fieldError(statePath)}
          invalid={isFieldTouched(statePath) && !!fieldError(statePath)}
        />
        <Input
          placeholder={t("purchase.creditCard.zipCode")}
          name={postalCodePath}
          value={stringValue(postalCodePath)}
          onChange={handleChange}
          onBlur={handleBlur}
          tooltip={fieldError(postalCodePath)}
          invalid={isFieldTouched(postalCodePath) && !!fieldError(postalCodePath)}
        />
      </AddressTripleFields>
    </Fields>
  );
};

const CreditCardFormInner = <TValues extends object>({
  formik,
  fields,
  cardSectionHeader,
  cardSectionFooter,
  fieldsTopSpacing = "default",
}: CreditCardFormProps<TValues>) => {
  const { t } = useTranslation("common");
  const fieldPaths: CreditCardFieldPaths = {
    ...DEFAULT_FIELD_PATHS,
    ...fields,
    billing: {
      ...DEFAULT_FIELD_PATHS.billing,
      ...fields?.billing,
    },
  };
  const cardFieldPaths = {
    name: fieldPaths.name,
    number: fieldPaths.number,
    expiration: fieldPaths.expiration,
    cvv: fieldPaths.cvv,
  };

  return (
    <CardForm>
      <CardSection>
        {cardSectionHeader === undefined ? (
          <SectionTitle>{t("common.creditCard")}</SectionTitle>
        ) : (
          cardSectionHeader
        )}
        <CardSectionFields $topSpacing={fieldsTopSpacing}>
          <CreditCardWidgetFields formik={formik} fieldPaths={cardFieldPaths} />
          <SecureNotice>{t("purchase.creditCard.secureNotice")}</SecureNotice>
          {cardSectionFooter}
        </CardSectionFields>
      </CardSection>
      <AddressSection>
        <SectionTitle>{t("purchase.creditCard.billingAddress")}</SectionTitle>
        <BillingAddressFields formik={formik} fieldPaths={fieldPaths.billing} />
      </AddressSection>
    </CardForm>
  );
};

const CreditCardForm = React.memo(CreditCardFormInner) as typeof CreditCardFormInner;

export default CreditCardForm;
